import React, { useState } from "react";
import { useLocation, Link, matchPath } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { FaCog, FaShareAlt } from "react-icons/fa";
import { FiGrid } from "react-icons/fi";

const routes = [
  { path: "/", label: "Login" },
  { path: "/survey-list", label: "Survey List" },
  { path: "/create-Survey", label: "Create Survey" },
  { path: "/add-questions/:assessmentUID", label: "Add Questions" },
  {
    path: "/Participant-Response/:assessmentUID",
    label: "Participant Response",
  },
  { path: "/manage-survey", label: "Manage Survey" },
  { path: "/Manage-Responder/:assessmentUID", label: "Manage Responder" },
];

export default function Layout({ children, surveyData }) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("Survey");

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const pathnames = location.pathname.split("/").filter((x) => x);

  const isAddQuestionPage = matchPath(
    "/add-questions/:assessmentUID",
    location.pathname
  );

  // Breadcrumbs
  const crumbs = [];
  let accumulatedPath = "";
  for (let i = 0; i < pathnames.length; i++) {
    accumulatedPath += `/${pathnames[i]}`;
    const route = routes.find((r) => matchPath(r.path, accumulatedPath));
    if (route) {
      let label = route.label;
      if (
        surveyData &&
        (route.path.includes(":assessmentUID") ||
          route.path.includes(":assessmentID"))
      ) {
        label = `${route.label}: ${surveyData.assessmentName}`;
      }
      crumbs.push({ label, to: accumulatedPath });
    } else if (i === pathnames.length - 1) {
      crumbs.push({ label: pathnames[i], to: accumulatedPath });
    }
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      {/*  Custom Header for Add Questions Page */}
      {isAddQuestionPage ? (
        <div className="bg-[#f1f6f3] border-b">
          <div className="flex justify-between items-center px-8 py-2">
            {/* Breadcrumbs */}
            <nav className="text-base text-gray-800 font-medium">
              <ol className="flex items-center gap-1">
                <li>
                  <Link to="/survey-list" className="hover:text-gray-900">
                    Home
                  </Link>
                </li>
                {crumbs.map((crumb, idx) => (
                  <li key={idx} className="flex items-center">
                    <span className="mx-1">/</span>
                    {idx === crumbs.length - 1 ? (
                      <span className="text-gray-800 font-semibold truncate max-w-[200px]">
                        {crumb.label}
                      </span>
                    ) : (
                      <Link
                        to={crumb.to}
                        className="hover:text-gray-900 truncate max-w-[200px]"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>

            {/* Middle Tabs */}
            <div className="flex gap-8 font-semibold text-gray-800">
              {["Insights", "Survey", "Reports"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-2 text-base transition-colors ${
                    activeTab === tab
                      ? "border-b-2 border-[#3bb290] text-black font-bold"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/*  Right Icons */}
            <div className="flex gap-4 items-center">
              <button
                className="p-2 bg-[#3bb290] text-white rounded-md hover:bg-[#2fb07f] shadow-sm"
                onClick={openSidebar}
              >
                <FaCog />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded-md text-gray-700 transition">
                <FaShareAlt />
              </button>
              <button className="p-2 hover:bg-gray-200 rounded-md text-gray-700 transition">
                <FiGrid />
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Default Breadcrumb Layout
        <nav className="bg-gray-50 p-4 border-b">
          <ol className="flex items-center gap-1 text-sm text-gray-500">
            <li>
              <Link to="/survey-list" className="hover:text-gray-700">
                Home
              </Link>
            </li>
            {crumbs.map((crumb, idx) => (
              <li key={idx} className="flex items-center">
                <span className="mx-1">/</span>
                {idx === crumbs.length - 1 ? (
                  <span className="text-gray-700 font-medium">
                    {crumb.label}
                  </span>
                ) : (
                  <Link to={crumb.to} className="hover:text-gray-700">
                    {crumb.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/*  Pass tab state to children (like AddQuestions) */}
      <main className="flex-grow p-4 pb-0">
        {React.cloneElement(children, {
          activeTab,
          setActiveTab,
          isSidebarOpen,
          onOpenSidebar: openSidebar,
          closeSidebar,
        })}
      </main>

      <Footer />
    </div>
  );
}
